import { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Title,
  TextInput,
  Select,
  Textarea,
  Button,
  Stack,
  PasswordInput,
  Group,
  Alert,
  Text,
  Accordion,
  Checkbox,
  Badge,
  Divider,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle } from "@tabler/icons-react";
import { useVendorProfile, useUpdateVendorProfile } from "../hooks/useVendors";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { vendorCategoriesApi, VendorCategory } from "../../../api/vendorCategories";
import { vendorsApi } from "../../../api/vendors";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconSend, IconClock } from "@tabler/icons-react";

export function VendorProfile() {
  const { data: profile, isLoading } = useVendorProfile();
  const updateProfile = useUpdateVendorProfile();
  const queryClient = useQueryClient();
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [selectedSubcategories, setSelectedSubcategories] = useState<Set<string>>(new Set());
  const [savingCategories, setSavingCategories] = useState(false);

  // Check if profile is ready for submission
  const { data: profileReady, refetch: refetchProfileReady } = useQuery({
    queryKey: ["vendor-profile-ready"],
    queryFn: () => vendorsApi.checkProfileReady(),
    enabled: !!profile && profile.status === "REGISTERED",
  });

  // Submit for approval mutation
  const submitForApprovalMutation = useMutation({
    mutationFn: () => vendorsApi.submitForApproval(),
    onSuccess: (data) => {
      // Update the profile data immediately with the new status
      queryClient.setQueryData(["vendor", "profile"], data.vendor);
      queryClient.invalidateQueries({ queryKey: ["vendor", "profile"] });
      queryClient.invalidateQueries({ queryKey: ["vendor-profile-ready"] });
      notifications.show({
        title: "Success",
        message: "Your profile has been submitted for approval. You will be notified once it's reviewed.",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to submit for approval",
        color: "red",
      });
    },
  });

  // Fetch available categories
  const { data: availableCategories, isLoading: categoriesLoading } = useQuery({
    queryKey: ["vendor-categories-available"],
    queryFn: () => vendorCategoriesApi.getAvailableCategories(),
  });

  // Fetch vendor's selected categories
  const { data: vendorCategories } = useQuery({
    queryKey: ["vendor-categories"],
    queryFn: () => vendorCategoriesApi.getVendorCategories(),
  });

  // Initialize selected subcategories from vendor's current selections
  useEffect(() => {
    if (vendorCategories) {
      const selected = new Set(vendorCategories.map((vc) => vc.subcategoryId));
      setSelectedSubcategories(selected);
    }
  }, [vendorCategories]);

  const updateCategoriesMutation = useMutation({
    mutationFn: (subcategoryIds: string[]) =>
      vendorCategoriesApi.updateVendorCategories({ subcategoryIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor-categories"] });
      notifications.show({
        title: "Success",
        message: "Service categories updated successfully",
        color: "green",
      });
      setSavingCategories(false);
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to update service categories",
        color: "red",
      });
      setSavingCategories(false);
    },
  });

  const handleSubcategoryToggle = (subcategoryId: string) => {
    const newSelected = new Set(selectedSubcategories);
    if (newSelected.has(subcategoryId)) {
      newSelected.delete(subcategoryId);
    } else {
      newSelected.add(subcategoryId);
    }
    setSelectedSubcategories(newSelected);
  };

  const handleSaveCategories = () => {
    setSavingCategories(true);
    updateCategoriesMutation.mutate(Array.from(selectedSubcategories));
  };

  const form = useForm({
    initialValues: {
      businessName: "",
      businessType: "" as "SOLE_PROPRIETOR" | "COMPANY" | "PARTNERSHIP" | "",
      registrationNumber: "",
      taxId: "",
      address: "",
      phone: "",
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validate: {
      businessName: (value) => null, // Optional - can be added later
      businessType: (value) => null, // Optional - can be added later
      newPassword: (value) => {
        if (showPasswordFields && !value) return "New password is required";
        if (value && value.length < 6) return "Password must be at least 6 characters";
        return null;
      },
      confirmPassword: (value, allValues) => {
        if (showPasswordFields && value !== allValues.newPassword) return "Passwords do not match";
        return null;
      },
    },
  });

  // Initialize form with profile data
  useEffect(() => {
    if (profile) {
      form.setValues({
        businessName: profile.businessName || "",
        businessType: profile.businessType || "",
        registrationNumber: profile.registrationNumber || "",
        taxId: profile.taxId || "",
        address: profile.address || "",
        phone: profile.phone || "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  }, [profile]);

  const handleSubmit = async (values: typeof form.values) => {
    try {
      const updateData: any = {
        businessName: values.businessName,
        businessType: values.businessType,
        registrationNumber: values.registrationNumber || undefined,
        taxId: values.taxId || undefined,
        address: values.address || undefined,
        phone: values.phone || undefined,
      };

      if (showPasswordFields && values.newPassword) {
        updateData.password = values.newPassword;
      }

      await updateProfile.mutateAsync(updateData);
      setShowPasswordFields(false);
      form.setFieldValue("currentPassword", "");
      form.setFieldValue("newPassword", "");
      form.setFieldValue("confirmPassword", "");
      // Refetch profile readiness after update
      refetchProfileReady();
    } catch (error: any) {
      // Error handled by mutation
    }
  };

  if (isLoading) {
    return (
      <Container size="md" py="xl">
        <div>Loading profile...</div>
      </Container>
    );
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Paper shadow="sm" p="xl" radius="md" withBorder>
          <Title order={2} mb="md">
            Vendor Profile
          </Title>

          {profile?.status === "PENDING" && (
            <Alert color="yellow" mb="md" icon={<IconClock size={16} />}>
              <Stack gap="sm">
                <Text size="sm" fw={500}>
                  Account Approval In Progress
                </Text>
                <Text size="sm">
                  Your profile has been submitted for approval and is currently under review by our procurement team. 
                  You will be notified via email once your account has been reviewed.
                </Text>
                <Badge color="yellow" size="lg" variant="light">
                  Status: Pending Approval
                </Badge>
              </Stack>
            </Alert>
          )}

          {profile?.status === "REGISTERED" && profileReady?.isReady && (
            <Alert color="green" mb="md" icon={<IconCheck size={16} />}>
              <Stack gap="sm">
                <Text size="sm" fw={500}>
                  Your profile is complete and ready for submission!
                </Text>
                <Text size="sm">
                  All required fields have been filled and mandatory documents have been uploaded. 
                  Click the button below to submit your profile for admin approval.
                </Text>
                <Button
                  leftSection={<IconSend size={16} />}
                  color="green"
                  onClick={() => submitForApprovalMutation.mutate()}
                  loading={submitForApprovalMutation.isPending}
                >
                  Send for Account Approval
                </Button>
              </Stack>
            </Alert>
          )}

          {profile?.status === "REGISTERED" && !profileReady?.isReady && (
            <Alert color="blue" mb="md" icon={<IconAlertCircle size={16} />}>
              <Text size="sm">
                Please complete all profile fields (Business Name, Business Type, Registration Number, Tax ID, Address, Phone) 
                and upload the required documents (Business Registration and Company Profile) to submit for approval.
              </Text>
            </Alert>
          )}

          {(!profile?.businessName || !profile?.businessType) && profile?.status !== "REGISTERED" && (
            <Alert color="yellow" mb="md" icon={<IconAlertCircle size={16} />}>
              <Text size="sm">
                Please complete your business details to participate in tenders. Business name and type are required for bid submissions.
              </Text>
            </Alert>
          )}

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <TextInput
                label="Business Name"
                placeholder="Your company name"
                {...form.getInputProps("businessName")}
              />

              <Select
                label="Business Type"
                placeholder="Select business type"
                data={[
                  { value: "SOLE_PROPRIETOR", label: "Sole Proprietor" },
                  { value: "COMPANY", label: "Company" },
                  { value: "PARTNERSHIP", label: "Partnership" },
                ]}
                {...form.getInputProps("businessType")}
              />

              <TextInput
                label="Registration Number"
                placeholder="Business registration number"
                {...form.getInputProps("registrationNumber")}
              />

              <TextInput label="Tax ID" placeholder="Tax identification number" {...form.getInputProps("taxId")} />

              <Textarea label="Address" placeholder="Business address" {...form.getInputProps("address")} />

              <TextInput label="Phone" placeholder="Contact phone number" {...form.getInputProps("phone")} />

              <Group justify="flex-end" mt="md">
                <Button type="submit" loading={updateProfile.isPending} color="green">
                  Save Profile
                </Button>
              </Group>
            </Stack>
          </form>
        </Paper>

        {/* Service Categories Section */}
        <Paper shadow="sm" p="xl" radius="md" withBorder>
          <Title order={2} mb="md">
            Service Categories
          </Title>
          <Text size="sm" c="dimmed" mb="md">
            Select the types of services your business offers. You can select multiple subcategories.
          </Text>

          {categoriesLoading ? (
            <Text c="dimmed">Loading categories...</Text>
          ) : !availableCategories || availableCategories.length === 0 ? (
            <Alert color="blue" title="No Categories Available">
              <Text size="sm">
                No service categories have been set up yet. Please contact the administrator.
              </Text>
            </Alert>
          ) : (
            <Stack gap="md">
              <Accordion variant="separated" radius="md">
                {availableCategories.map((category) => (
                  <Accordion.Item key={category.id} value={category.id}>
                    <Accordion.Control>
                      <Group justify="space-between">
                        <Text fw={500}>{category.name}</Text>
                        <Badge size="sm" color="blue">
                          {category.subcategories.length} subcategor
                          {category.subcategories.length !== 1 ? "ies" : "y"}
                        </Badge>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      {category.subcategories.length === 0 ? (
                        <Text size="sm" c="dimmed">
                          No subcategories available for this category.
                        </Text>
                      ) : (
                        <Stack gap="sm" mt="sm">
                          {category.subcategories.map((subcategory) => (
                            <Checkbox
                              key={subcategory.id}
                              label={
                                <div>
                                  <Text fw={500}>{subcategory.name}</Text>
                                  {subcategory.description && (
                                    <Text size="xs" c="dimmed">
                                      {subcategory.description}
                                    </Text>
                                  )}
                                </div>
                              }
                              checked={selectedSubcategories.has(subcategory.id)}
                              onChange={() => handleSubcategoryToggle(subcategory.id)}
                            />
                          ))}
                        </Stack>
                      )}
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>

              {selectedSubcategories.size > 0 && (
                <div>
                  <Text size="sm" fw={500} mb="xs">
                    Selected Services ({selectedSubcategories.size}):
                  </Text>
                  <Group gap="xs">
                    {availableCategories.flatMap((category) =>
                      category.subcategories
                        .filter((sub) => selectedSubcategories.has(sub.id))
                        .map((sub) => (
                          <Badge key={sub.id} color="green" size="sm">
                            {category.name} - {sub.name}
                          </Badge>
                        ))
                    )}
                  </Group>
                </div>
              )}

              <Group justify="flex-end" mt="md">
                <Button
                  onClick={handleSaveCategories}
                  loading={savingCategories}
                  color="green"
                  disabled={selectedSubcategories.size === 0 && (!vendorCategories || vendorCategories.length === 0)}
                >
                  Save Service Categories
                </Button>
              </Group>
            </Stack>
          )}
        </Paper>

        {/* Password Change Section */}
        <Paper shadow="sm" p="xl" radius="md" withBorder>
          <Title order={2} mb="md">
            Change Password
          </Title>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              {showPasswordFields && (
                <Alert icon={<IconAlertCircle size={16} />} title="Change Password" color="green">
                  <Stack gap="sm" mt="xs">
                    <PasswordInput
                      label="New Password"
                      placeholder="Enter new password"
                      {...form.getInputProps("newPassword")}
                    />
                    <PasswordInput
                      label="Confirm Password"
                      placeholder="Confirm new password"
                      {...form.getInputProps("confirmPassword")}
                    />
                  </Stack>
                </Alert>
              )}

              <Group justify="space-between" mt="md">
                <Button
                  variant="light"
                  color="green"
                  onClick={() => {
                    setShowPasswordFields(!showPasswordFields);
                    if (showPasswordFields) {
                      form.setFieldValue("newPassword", "");
                      form.setFieldValue("confirmPassword", "");
                    }
                  }}
                >
                  {showPasswordFields ? "Cancel Password Change" : "Change Password"}
                </Button>
                <Button type="submit" loading={updateProfile.isPending} color="green">
                  Save Changes
                </Button>
              </Group>
            </Stack>
          </form>
        </Paper>
      </Stack>
    </Container>
  );
}

